import { Injectable, inject } from '@angular/core';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import { utcIso } from '@core/helpers/slot-series.helper';
import {
  Binding,
  BindingCandidates,
  ChannelVocabulary,
  DataSources,
  MeasurementPoint,
  MeasurementPointDetail,
  MeasurementPoints,
} from '@core/models/installation.model';
import {
  ClearBinding,
  NewMeasurementPoint,
  RetractBinding,
  SetBinding,
  UpdateMeasurementPoint,
} from '@core/models/installation-write.model';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError, map } from 'rxjs';

// customer-func writes its binding times without an offset, and they are UTC; parsed as local
// time they would shift by the browser's offset. They are made explicit here, once.
function withUtcTimes(binding: Binding): Binding {
  return {
    ...binding,
    validFrom: utcIso(binding.validFrom),
    validTo: binding.validTo === null ? null : utcIso(binding.validTo),
    retracted:
      binding.retracted === null
        ? null
        : { ...binding.retracted, retractedAt: utcIso(binding.retracted.retractedAt) },
  };
}

function pointWithUtcTimes<T extends MeasurementPoint>(point: T): T {
  return { ...point, binding: point.binding === null ? null : withUtcTimes(point.binding) };
}

function detailWithUtcTimes(detail: MeasurementPointDetail): MeasurementPointDetail {
  return { ...pointWithUtcTimes(detail), bindings: detail.bindings.map(withUtcTimes) };
}

// The points of an installation, their bindings and the channels at its location. Every request
// fails with an InstallationRefusal.
@Injectable({
  providedIn: 'root',
})
export class InstallationDataSourcesService {
  private readonly apiService = inject(ApiService);

  dataSources(installationOid: string): Observable<DataSources> {
    return this.read<DataSources>(`/installations/${installationOid}/data-sources`).pipe(
      map(sources => ({ ...sources, points: sources.points.map(pointWithUtcTimes) })),
    );
  }

  // The data sources leave hidden points out; this lists them beside the active ones.
  allPoints(installationOid: string): Observable<MeasurementPoint[]> {
    return this.read<MeasurementPoints>(`/installations/${installationOid}/points`, {
      state: 'all',
    }).pipe(map(response => response.points.map(pointWithUtcTimes)));
  }

  point(installationOid: string, pointOid: string): Observable<MeasurementPointDetail> {
    return this.read<MeasurementPointDetail>(
      `/installations/${installationOid}/points/${pointOid}`,
    ).pipe(map(detailWithUtcTimes));
  }

  // Off-catalogue channels are never asked for: the editor does not offer them.
  candidates(installationOid: string, pointOid: string): Observable<BindingCandidates> {
    return this.read<BindingCandidates>(
      `/installations/${installationOid}/points/${pointOid}/candidates`,
    );
  }

  channelVocabulary(): Observable<ChannelVocabulary> {
    return this.read<ChannelVocabulary>('/installations/channel-vocabulary');
  }

  setBinding(
    installationOid: string,
    pointOid: string,
    body: SetBinding,
  ): Observable<MeasurementPointDetail> {
    return this.apiService
      .putRaw<MeasurementPointDetail>(
        `/installations/${installationOid}/points/${pointOid}/binding`,
        body,
        explainedInPlace(),
      )
      .pipe(map(detailWithUtcTimes), catchError(rethrowAsRefusal));
  }

  clearBinding(
    installationOid: string,
    pointOid: string,
    body: ClearBinding,
  ): Observable<MeasurementPointDetail> {
    return this.apiService
      .deleteRaw<MeasurementPointDetail>(
        `/installations/${installationOid}/points/${pointOid}/binding`,
        body,
        explainedInPlace(),
      )
      .pipe(map(detailWithUtcTimes), catchError(rethrowAsRefusal));
  }

  retract(
    installationOid: string,
    pointOid: string,
    bindingOid: string,
    body: RetractBinding,
  ): Observable<MeasurementPointDetail> {
    return this.apiService
      .postRaw<MeasurementPointDetail>(
        `/installations/${installationOid}/points/${pointOid}/bindings/${bindingOid}/retract`,
        body,
        { context: explainedInPlace() },
      )
      .pipe(map(detailWithUtcTimes), catchError(rethrowAsRefusal));
  }

  createPoint(installationOid: string, body: NewMeasurementPoint): Observable<MeasurementPoint> {
    return this.apiService
      .postRaw<MeasurementPoint>(`/installations/${installationOid}/points`, body, {
        context: explainedInPlace(),
      })
      .pipe(map(pointWithUtcTimes), catchError(rethrowAsRefusal));
  }

  renamePoint(
    installationOid: string,
    pointOid: string,
    body: UpdateMeasurementPoint,
  ): Observable<MeasurementPoint> {
    return this.apiService
      .patchRaw<MeasurementPoint>(
        `/installations/${installationOid}/points/${pointOid}`,
        body,
        explainedInPlace(),
      )
      .pipe(map(pointWithUtcTimes), catchError(rethrowAsRefusal));
  }

  private read<T>(path: string, params: Record<string, unknown> = {}): Observable<T> {
    return this.apiService
      .getRaw<T>(path, params, explainedInPlace())
      .pipe(catchError(rethrowAsRefusal));
  }
}
